import { ModelIteratorBVH } from '../../../ModelIteratorBVH';
import { LoadGeometryTask } from "./LoadGeometryTask";
import { LoadRcsNodeTask } from "./LoadRcsNodeTask";
import { OutOfCoreTileManager } from '../../OutOfCoreTileManager';
import { rcsWorkerPool } from "../../../../../file-loaders/rcs/RcsWorkerPool";
import { StageNames } from '../defaultStageConfiguration';

/** @import {BvhNode} from "../../BvhNode" */

/**
 * Creates a task that waits until the geometry has been loaded (once the fraglist has been loaded).
 *
 * @param {BvhNode} node - The ID of the BVH node
 * @param {Stage} stage - The stage of the task
 */
export function initializeGeometryLoadedTasks(node, stage) {
  const bvhModelIterator = node.model.getIterator();

  if (!(bvhModelIterator instanceof ModelIteratorBVH)) {
    console.error("ModelIterator is not a BVH iterator");
    return;
  }

  if (node.model.loader?.supportsGeometryLoadTask) {
    node.addTask(new LoadGeometryTask(node), stage);
  }
}

/**
 * Creates a task that loads RCS octree node data on-demand
 *
 * @param {BvhNode} node - The BVH node
 * @param {Stage} stage - The stage of the task
 */
export function initializeRcsGeometryLoadedTasks(node, stage) {
  const bvhModelIterator = node.model.getIterator();

  if (!(bvhModelIterator instanceof ModelIteratorBVH)) {
    console.error("ModelIterator is not a BVH iterator");
    return;
  }

  node.addTask(new LoadRcsNodeTask(node), stage);
}

/**
 * @param {BvhNode} node - The BVH node to check prerequisites for
 * @returns {boolean} - True if all prerequisites are met, false otherwise
 */
export function geometryLoadedPrerequisites(node) {

  if (!node.model.loader?.supportsGeometryLoadTask) {
    return true;
  }

  // It can happen, that the BVH is already initialized and ready to provide batches
  // to the loader, before the request objects in the loader have been initialized.
  // Specifically, if there is a selective loading controller active, it can delay the
  // initialization of fragments and then the requests will not be created. So we have
  // to wait until the selective loading controller is ready.
  const selectiveLoadingController = node.model.loader._selectiveLoadingController;
  if (selectiveLoadingController?.isActive && !selectiveLoadingController.isReady) {
    return false;
  }

  const geomCache = node.model.loader.geomCache();
  OutOfCoreTileManager.notifyHadDownloadTasks();

  if (!geomCache.hasBandwidthBudget()) {
    return false;
  }
  return true;
}

/**
 * Prerequisites for RCS geometry loading.
 *
 * Returns one of three values, consumed by `getPromotionCandidateForStage`:
 *   - `false`              — global in-flight cap exceeded; skip this stage entirely this tick.
 *   - `{ blockers: BvhNode[] }` — this LOD-0 leaf has incomplete parent inner nodes; the caller
 *                             parks the leaf on those blockers and re-checks the next heap-min.
 *   - `true`               — ready to promote.
 *
 * The `{ blockers }` path is only ever reached for RCS LOD-0 umbrella leaves; all other nodes
 * go directly to the `true` branch.
 *
 * Why LOD-0 leaves must wait for their parent inner nodes:
 *   Priorities cannot enforce this ordering. A parent that fetches data goes into an async
 *   promotion, and the scheduler keeps promoting lower-priority nodes while that promotion is
 *   pending — so a LOD-0 leaf can be promoted long before its parents are done, no matter how
 *   much higher the parents' priority is. (Inner nodes and LOD-0 leaves are both "coarse
 *   nodes" and in fact share the same 1e5 priority boost.)
 *
 *   The leaf's synchronous allParentsCached check and HLOD pull-up in
 *   _loadFragmentWithHierarchicalLOD then race with parent completion: on warm loads the
 *   parent is not yet in innerNodesCachedSet, so the leaf needlessly re-runs the pull-up and
 *   re-fetches the SVO payload from the network instead of relying on the parent's cached
 *   inner-node geometry — defeating the cache.
 *
 *   Parking each leaf until all of its parent inner nodes have completed their
 *   GEOMETRY_LOADED tasks removes the race and makes cache population deterministic.
 *
 * @param {BvhNode} node - The BVH node to check prerequisites for
 * @returns {boolean | { blockers: BvhNode[] }}
 */
export function rcsGeometryLoadedPrerequisites(node) {
  // Cap in-flight RCS network/decoding backlog. Without this, a long queue of outstanding
  // fetches for an old view must drain before the loader can prioritize new requests for
  // the current viewpoint, so camera moves feel unresponsive.
  // Returning `false` (not `{ blockers }`) tells the selector to skip the whole stage this
  // tick — this is a global transient condition, not a per-node dependency.
  const MEGABYTES = 1024 * 1024;
  if (rcsWorkerPool.inFlightRequestsSize >= 20 * MEGABYTES) {
    return false;
  }

  const pkg = node.model.getData();
  if (pkg && pkg.getNodeLodLevel(node.nodeId) === 0) {
    const parentLodRanges = pkg.getNodeParentLodRanges(node.nodeId);
    if (parentLodRanges?.length > 0) {
      const bvhNodes = node.outOfCoreTileManager.bvhNodes;
      /** @type {Set<BvhNode>} */
      const seenBlockers = new Set();
      for (const { bvhNodeId } of parentLodRanges) {
        // Every target of a leaf's parent LOD ranges receives pulled-up points.
        if (!pkg.isInnerNode(bvhNodeId)) continue;
        const parentNode = bvhNodes[bvhNodeId];
        // Block until the parent's GEOMETRY_LOADED tasks have fully completed.
        // During promotion the parent stays in INITIAL until execute() returns, so
        // checking currentStage === GEOMETRY_LOADED misses the entire in-flight window.
        if (!parentNode.isStageCompleted(StageNames.GEOMETRY_LOADED)) {
          seenBlockers.add(parentNode);
        }
      }
      if (seenBlockers.size > 0) {
        return { blockers: Array.from(seenBlockers) };
      }
    }
  }

  return true;
}